import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, MinusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import ZoneDistributorSelector from '../../components/supply/ZoneDistributorSelector';
import SupplySourceSelector from '../../components/supply/SupplySourceSelector';

/**
 * Formulaire de création de demande produit — animateurs, chefs de zone, marketing.
 * Animateur / chef de zone : distributeur de leur zone ; pas d'étape marketing, le créateur
 * prend la demande en charge d'office (décharge, et attribution si le motif l'exige).
 * Marketing : site interne ou zone (comme à la validation) ; la demande est validée dans la
 * foulée (n° d'attribution, document signé) et passe à l'étape suivante.
 * La route est protégée par ProtectedRoute dans App.js — pas de redirection ici.
 */
const CreateAnimateurRequest = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isMarketing = currentUser?.role === 'marketing';
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [supplySource, setSupplySource] = useState(null);

  // Motif state (managed outside Formik)
  const [motifs, setMotifs] = useState([]);
  const [selectedMotifValue, setSelectedMotifValue] = useState('');
  const [motifError, setMotifError] = useState('');
  const [newMotifLabel, setNewMotifLabel] = useState('');
  const [addingMotif, setAddingMotif] = useState(false);

  // Charger les motifs au montage
  useEffect(() => {
    // Business Unit a son propre formulaire. Animation / Activation n'est renvoyé par
    // le serveur qu'aux rôles autorisés à le créer (ANIMATION_CREATOR_ROLES).
    api.get('/api/motifs')
      .then(res => setMotifs((res.data.data || []).filter(m => m.workflowType !== 'business_unit')))
      .catch(() => toast.error('Erreur lors du chargement des motifs'));
  }, []);

  // Charger les produits au montage
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/api/products');
        setProducts(response.data.data || []);
      } catch {
        toast.error('Erreur lors du chargement des produits');
      }
    };

    fetchProducts();
  }, []);

  // Ajouter un nouveau motif
  const handleAddMotif = async () => {
    if (!newMotifLabel.trim()) return;
    try {
      setAddingMotif(true);
      const res = await api.post('/api/motifs', { label: newMotifLabel.trim() });
      const newMotif = res.data.data;
      setMotifs(prev => [newMotif, ...prev]);
      setSelectedMotifValue(newMotif.value);
      setNewMotifLabel('');
      toast.success(`Motif "${newMotif.label}" ajouté`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'ajout du motif');
    } finally {
      setAddingMotif(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      organization: '',
      selectedProducts: [{ productId: '', quantity: 1, _rowId: crypto.randomUUID() }],
      additionalInfo: '',
      supplySource: null,
    },
    validationSchema: Yup.object({
      organization: Yup.string().required('L\'organisation est requise'),
      selectedProducts: Yup.array()
        .of(
          Yup.object().shape({
            productId: Yup.string().required('Produit requis'),
            quantity: Yup.number()
              .required('Quantité requise')
              .positive('La quantité doit être positive')
              .integer('La quantité doit être un nombre entier'),
          })
        )
        .min(1, 'Au moins un produit doit être sélectionné'),
      additionalInfo: Yup.string(),
      supplySource: Yup.object().nullable().test(
        'is-source-selected',
        isMarketing ? 'Veuillez sélectionner une source d\'approvisionnement' : 'Veuillez sélectionner un distributeur',
        value => isMarketing
          ? !!(value && value.category && value.source)
          : !!(value && value.category === 'distributor' && value.source && value.source.id)
      ),
    }),
    onSubmit: async (values) => {
      // Validate motif manually
      if (!selectedMotifValue) {
        setMotifError('Veuillez sélectionner un motif');
        return;
      }
      setMotifError('');

      try {
        setLoading(true);

        const formData = new FormData();
        formData.append('name', currentUser?.name || '');
        formData.append('email', currentUser?.email || '');
        formData.append('organization', values.organization);
        formData.append('requestType', selectedMotifValue);
        formData.append('reason', selectedMotifValue);
        formData.append('selectedProducts', JSON.stringify(values.selectedProducts));
        formData.append('additionalInfo', values.additionalInfo);

        if (values.supplySource) {
          formData.append('supplySource', JSON.stringify(values.supplySource));
        } else {
          toast.error(isMarketing ? 'Veuillez sélectionner une source d\'approvisionnement' : 'Veuillez sélectionner un distributeur de votre zone');
          setLoading(false);
          return;
        }

        if (selectedFile) {
          formData.append('attachment', selectedFile);
        }

        const response = await api.post('/api/requests', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Marketing : validation immédiate de sa propre demande (même endpoint que la modale) —
        // n° d'attribution, document d'attribution signé, passage à l'étape suivante.
        if (isMarketing) {
          try {
            const validated = await api.marketingValidation.validate(response.data.id, {
              supplySource: values.supplySource,
              comment: 'Source d\'approvisionnement sélectionnée à la création',
            });
            // Animation / Activation : aucune attribution n'est écrite.
            toast.success(validated.data?.data?.document
              ? 'Demande créée et validée, document d\'attribution généré'
              : 'Demande créée et validée');
          } catch (valErr) {
            // La demande existe et reste à l'étape marketing : validable depuis le détail.
            toast.error(valErr.response?.data?.error || 'Demande créée, mais la validation marketing a échoué. Validez-la depuis le détail.');
          }
        } else {
          // Prise en charge d'office : le serveur dit où en est la demande.
          toast.success(response.data.message || 'Demande produit créée avec succès');
        }
        navigate(`/requests/${response.data.id}`);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erreur lors de la création de la demande');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleSupplySourceChange = (value) => {
    setSupplySource(value);
    formik.setFieldValue('supplySource', value);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFileError('');

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError('Le fichier est trop volumineux. Taille maximale: 5MB');
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setFileError('Type de fichier non supporté. Formats acceptés: PDF, JPEG, PNG');
        return;
      }

      setSelectedFile(file);
    }
  };

  const addProductRow = () => {
    formik.setFieldValue('selectedProducts', [
      ...formik.values.selectedProducts,
      { productId: '', quantity: 1, _rowId: crypto.randomUUID() },
    ]);
  };

  const removeProductRow = (index) => {
    if (formik.values.selectedProducts.length > 1) {
      const updatedProducts = [...formik.values.selectedProducts];
      updatedProducts.splice(index, 1);
      formik.setFieldValue('selectedProducts', updatedProducts);
    }
  };

  const handleQuantityChange = (index, value) => {
    const qty = Math.max(1, parseInt(value) || 1);
    const updatedProducts = formik.values.selectedProducts.map((p, i) =>
      i === index ? { ...p, quantity: qty } : p
    );
    formik.setFieldValue('selectedProducts', updatedProducts);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Créer une demande produit</h1>
        <button
          onClick={() => navigate('/requests')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 cursor-pointer transition-colors duration-150"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={formik.handleSubmit} className="space-y-6">

          {/* Informations du demandeur */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du demandeur</h3>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">

              {/* Nom complet — pré-rempli, non modifiable */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nom complet
                </label>
                <div className="mt-1 flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 text-sm">
                  {currentUser?.name}
                </div>
                <p className="mt-1 text-xs text-gray-500">Renseigné depuis votre profil</p>
              </div>

              {/* Adresse email — pré-remplie, non modifiable */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <div className="mt-1 flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 text-sm">
                  {currentUser?.email}
                </div>
                <p className="mt-1 text-xs text-gray-500">Renseigné depuis votre profil</p>
              </div>

              {/* Organisation */}
              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
                  Organisation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  className={`mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                    formik.touched.organization && formik.errors.organization ? 'border-red-500' : ''
                  }`}
                  value={formik.values.organization}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Ex: Supermarché Ivoire"
                />
                {formik.touched.organization && formik.errors.organization && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.organization}</p>
                )}
              </div>

            </div>
          </div>

          {/* Motif de la demande */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Motif de la demande</h3>

            {/* Sélectionner un motif existant */}
            <div className="mb-4">
              <label htmlFor="motif" className="block text-sm font-medium text-gray-700">
                Motif <span className="text-red-500">*</span>
              </label>
              <select
                id="motif"
                value={selectedMotifValue}
                onChange={e => {
                  setSelectedMotifValue(e.target.value);
                  if (e.target.value) setMotifError('');
                }}
                className={`mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm cursor-pointer ${
                  motifError ? 'border-red-500' : ''
                }`}
              >
                <option value="">Sélectionnez un motif</option>
                {motifs.map(m => (
                  <option key={m._id} value={m.value}>
                    {m.isCustom ? `✦ ${m.label}` : m.label}
                  </option>
                ))}
              </select>
              {motifError && <p className="mt-2 text-sm text-red-600">{motifError}</p>}
            </div>

            {/* Ajouter un nouveau motif : marketing seulement, animateurs et chefs de zone
                choisissent dans la liste (le serveur refuse aussi, MOTIF_CREATOR_ROLES). */}
            {isMarketing && (
              <>
                <div className="flex items-end gap-2 pt-3 border-t border-gray-100">
                  <div className="flex-1">
                    <label htmlFor="newMotif" className="block text-sm font-medium text-gray-700">
                      Nouveau motif
                    </label>
                    <input
                      id="newMotif"
                      type="text"
                      value={newMotifLabel}
                      onChange={e => setNewMotifLabel(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddMotif();
                        }
                      }}
                      placeholder="Ex: Partenaire événementiel"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddMotif}
                    disabled={!newMotifLabel.trim() || addingMotif}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {addingMotif ? 'Ajout...' : 'Ajouter'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  ✦ indique un motif personnalisé
                </p>
              </>
            )}
          </div>

          {/* Produits demandés */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Produits demandés</h3>

            {formik.values.selectedProducts.map((product, index) => (
              <div key={product._rowId} className="flex items-center space-x-2 mb-2">
                <div className="flex-grow">
                  <select
                    name={`selectedProducts[${index}].productId`}
                    value={product.productId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm cursor-pointer ${
                      formik.touched.selectedProducts?.[index]?.productId &&
                      formik.errors.selectedProducts?.[index]?.productId
                        ? 'border-red-500'
                        : ''
                    }`}
                  >
                    <option value="">Sélectionnez un produit</option>
                    {Array.isArray(products) && products.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.selectedProducts?.[index]?.productId &&
                   formik.errors.selectedProducts?.[index]?.productId && (
                    <p className="mt-2 text-sm text-red-600">
                      {formik.errors.selectedProducts[index].productId}
                    </p>
                  )}
                </div>

                <div className="w-32">
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="bg-gray-200 p-1 rounded-l-md cursor-pointer hover:bg-gray-300 transition-colors duration-150"
                      onClick={() => handleQuantityChange(index, product.quantity - 1)}
                    >
                      <MinusIcon className="h-5 w-5 text-gray-600" />
                    </button>
                    <input
                      type="number"
                      name={`selectedProducts[${index}].quantity`}
                      value={product.quantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      onBlur={formik.handleBlur}
                      className={`form-input rounded-none border-l-0 border-r-0 text-center ${
                        formik.touched.selectedProducts?.[index]?.quantity &&
                        formik.errors.selectedProducts?.[index]?.quantity
                          ? 'border-red-500'
                          : ''
                      }`}
                      min="1"
                    />
                    <button
                      type="button"
                      className="bg-gray-200 p-1 rounded-r-md cursor-pointer hover:bg-gray-300 transition-colors duration-150"
                      onClick={() => handleQuantityChange(index, product.quantity + 1)}
                    >
                      <PlusIcon className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                  {formik.touched.selectedProducts?.[index]?.quantity &&
                   formik.errors.selectedProducts?.[index]?.quantity && (
                    <p className="mt-2 text-sm text-red-600">
                      {formik.errors.selectedProducts[index].quantity}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeProductRow(index)}
                  className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 cursor-pointer transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={formik.values.selectedProducts.length === 1}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addProductRow}
              className="mt-2 flex items-center text-sm text-blue-600 hover:text-blue-800 cursor-pointer transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Ajouter un produit
            </button>

            {formik.touched.selectedProducts &&
             typeof formik.errors.selectedProducts === 'string' && (
              <p className="mt-2 text-sm text-red-600">{formik.errors.selectedProducts}</p>
            )}
          </div>

          {/* Source d'approvisionnement */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Source d'approvisionnement</h3>
            <p className="text-sm text-gray-600 mb-4">
              {isMarketing
                ? 'Sélectionnez le site interne ou la zone qui fournira les produits demandés.'
                : 'Sélectionnez le distributeur de votre zone qui fournira les produits demandés.'}
            </p>
            {isMarketing ? (
              <SupplySourceSelector onChange={handleSupplySourceChange} value={supplySource} disabled={loading} />
            ) : (
              <ZoneDistributorSelector onChange={handleSupplySourceChange} value={supplySource} disabled={loading} />
            )}
            {formik.touched.supplySource && formik.errors.supplySource && (
              <p className="mt-2 text-sm text-red-600">{formik.errors.supplySource}</p>
            )}
          </div>

          {/* Informations supplémentaires */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informations supplémentaires</h3>

            <div className="mb-6">
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">
                Informations supplémentaires (optionnel)
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                rows={3}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={formik.values.additionalInfo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Précisez tout détail utile pour le traitement de votre demande..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Pièce jointe (optionnel)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Télécharger un fichier</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                    </label>
                    <p className="pl-1">ou glisser-déposer</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    PDF, PNG, JPG jusqu'à 5MB
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-green-600">
                      Fichier sélectionné: {selectedFile.name}
                    </p>
                  )}
                  {fileError && (
                    <p className="text-sm text-red-600">{fileError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              {loading ? 'Création en cours...' : 'Créer la demande'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreateAnimateurRequest;
